import CloseIcon from "@mui/icons-material/Close";
import type { PopoverActions } from "@mui/material";
import {
  type FC,
  type RefObject,
  useCallback,
  useId,
  useRef,
  useState,
} from "react";

import { METHODOLOGY_LINK } from "~/constants/socials";
import { CiteTab } from "~/feature/citeEmbed/components/CiteTab";
import { EmbedTab } from "~/feature/citeEmbed/components/EmbedTab";
import { StayOnTop } from "~/feature/citeEmbed/components/StayOnTop";
import {
  CITE_EMBED_FOOTER,
  CITE_EMBED_TABS,
  CITE_EMBED_TITLE,
  METHODOLOGY_LABEL,
} from "~/feature/citeEmbed/constants";
import {
  CiteEmbedTabEnum,
  type ICitationLeader,
  type ICiteEmbedEvent,
} from "~/feature/citeEmbed/types";
import type { IMarket } from "~/types/event";

import * as Styled from "./CiteEmbedPopover.styles";

interface ICiteEmbedPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  event: ICiteEmbedEvent;
  markets: IMarket[];
  defaultSelection: string[];
  leader: ICitationLeader | null;
}

interface ICiteEmbedContentProps extends Omit<
  ICiteEmbedPopoverProps,
  "anchorEl"
> {
  titleId: string;
  action: RefObject<PopoverActions | null>;
}

const CiteEmbedContent: FC<ICiteEmbedContentProps> = ({
  onClose,
  event,
  markets,
  defaultSelection,
  leader,
  titleId,
  action,
}) => {
  const [tab, setTab] = useState(CiteEmbedTabEnum.CITE);
  const [isEmbedMounted, setEmbedMounted] = useState(false);
  const tabsId = useId();
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || typeof ResizeObserver === "undefined") return;

      const observer = new ResizeObserver(() =>
        action.current?.updatePosition()
      );
      observer.observe(node);

      return () => observer.disconnect();
    },
    [action]
  );

  const handleSelect = (value: CiteEmbedTabEnum) => {
    setTab(value);

    if (value === CiteEmbedTabEnum.EMBED) setEmbedMounted(true);
  };

  return (
    <Styled.Content ref={ref}>
      <Styled.Header>
        <Styled.Title id={titleId}>{CITE_EMBED_TITLE}</Styled.Title>
        <Styled.CloseButton type="button" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </Styled.CloseButton>
      </Styled.Header>

      <Styled.Tabs role="tablist" aria-labelledby={titleId}>
        {CITE_EMBED_TABS.map(({ value, label }) => (
          <Styled.Tab
            key={value}
            type="button"
            role="tab"
            id={`${tabsId}-${value}`}
            aria-selected={tab === value}
            aria-controls={`${tabsId}-${value}-panel`}
            active={tab === value}
            onClick={() => handleSelect(value)}
          >
            {label}
          </Styled.Tab>
        ))}
      </Styled.Tabs>

      <div
        role="tabpanel"
        id={`${tabsId}-${CiteEmbedTabEnum.CITE}-panel`}
        aria-labelledby={`${tabsId}-${CiteEmbedTabEnum.CITE}`}
        hidden={tab !== CiteEmbedTabEnum.CITE}
      >
        <CiteTab event={event} leader={leader} />
      </div>
      <div
        role="tabpanel"
        id={`${tabsId}-${CiteEmbedTabEnum.EMBED}-panel`}
        aria-labelledby={`${tabsId}-${CiteEmbedTabEnum.EMBED}`}
        hidden={tab !== CiteEmbedTabEnum.EMBED}
      >
        {isEmbedMounted && (
          <EmbedTab
            event={event}
            markets={markets}
            defaultSelection={defaultSelection}
          />
        )}
      </div>

      <StayOnTop />

      <Styled.Footer>
        {CITE_EMBED_FOOTER}{" "}
        <Styled.FooterLink to={METHODOLOGY_LINK}>
          {METHODOLOGY_LABEL}
        </Styled.FooterLink>
      </Styled.Footer>
    </Styled.Content>
  );
};

export const CiteEmbedPopover: FC<ICiteEmbedPopoverProps> = ({
  anchorEl,
  ...contentProps
}) => {
  const open = Boolean(anchorEl);
  const titleId = useId();
  const action = useRef<PopoverActions | null>(null);
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);

  if (open !== wasOpen) {
    setWasOpen(open);

    if (open) setSession((previous) => previous + 1);
  }

  return (
    <Styled.Popover
      open={open}
      anchorEl={anchorEl}
      onClose={contentProps.onClose}
      action={action}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": titleId,
        },
      }}
    >
      <CiteEmbedContent
        key={session}
        {...contentProps}
        titleId={titleId}
        action={action}
      />
    </Styled.Popover>
  );
};
